import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, db } from 'config/firebase';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { doc, updateDoc, arrayUnion, deleteDoc, getDoc } from 'firebase/firestore';
import { 
    Alert, 
    Button, Spinner, Card, Container, 
    Row, Col, Badge, Image, Accordion, ListGroup, 
    InputGroup, Form, FormControl, Modal } from 'react-bootstrap';
import PageHeader from 'components/common/PageHeader';
import MissingImage from './../../assets/merlin/missing_image.png';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import ShoppingCart from 'components/app/e-commerce/cart/ShoppingCart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt as pencil, faTrash as trash} from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify';
import useRoleData from 'hooks/useRoleData.js';
import * as fuzz from 'fuzzball'
import useSearchData from 'hooks/useSearchData';
import { getProductImage } from 'utils/getProductImage';
import { debounce } from 'lodash';

const Carts = () => {

    const { cartId } = useParams();
    const [viewingCartID, setViewingCartID] = useState("Loading...")
    const [editTitle, setEditTitle] = useState(false);
    const [deleteCartState, setDeleteCartState] = useState(false);
    const [user, authLoading] = useAuthState(auth);
    const navigate = useNavigate();
    const isValidCartId = cartId && cartId !== 'undefined';
    const [manualOrder, orderLoading, orderError, orderSnapshot] = useDocumentData(
        isValidCartId ? doc(db, "openOrders", cartId) : null
    );
    const [roleData, orgData, userLoading, userError] = useRoleData(user?.uid);
    const [fuzzyChoices, loading, error] = useSearchData(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [hideDiscontinued, setHideDiscontinued] = useState(true);

    const debouncedSearch = useCallback(
        debounce((term) => {
            setDebouncedSearchTerm(term);
        }, 350),
        []
    );

    function handleSearchChange(e) {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    }

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    async function searchProducts(searchTerm) {
        try {
            let choices = fuzzyChoices.filter((item) => item !== undefined);
            if (hideDiscontinued) {
                choices = choices.filter((item) => item.disc !== true);
            }
            const term = searchTerm.trim();

            // Check for exact SKU match first
            const skuMatch = choices.find((item) => item.sku === term);
            if (skuMatch) {
                setSearchResults([[skuMatch, 100, 0]]);
                return [[skuMatch, 100, 0]];
            }

            const options = {
                scorer: fuzz.token_set_ratio,
                processor: (choice) => choice.proc_sorted,
                limit: 25,
                cutoff: 80,
                unsorted: false,
                full_process: true,
            }

            const results = fuzz.extract(term, choices, options)

            setSearchResults(results);
            
            return results;
        } catch (error) {
            console.log(error);
        }

    }

    async function addToCurrentCart(hit) {
        try {
            const cartRef = doc(db, "openOrders", viewingCartID);
            const cartSnap = await getDoc(cartRef);

            if (!cartSnap.exists()) {
                toast.error("Cart not found", { theme: 'colored' });
                return;
            }

            const cartData = cartSnap.data();
            const items = cartData.items || [];
            const existingItemIndex = items.findIndex(item => item.sku === hit.sku);

            if (existingItemIndex !== -1) {
                // Item with same SKU exists - increment quantity
                items[existingItemIndex].quantity = (items[existingItemIndex].quantity || 1) + 1;
                await updateDoc(cartRef, { items });
                toast.success(`${hit.name} quantity updated to ${items[existingItemIndex].quantity}`, {
                    theme: 'colored'
                });
            } else {
                // New item - add to cart
                await updateDoc(cartRef, {
                    items: arrayUnion({
                        sku: hit.sku,
                        title: hit?.name,
                        cost: '0.00',
                        picture: getProductImage(hit) || MissingImage,
                        quantity: 1
                    })
                });
                toast.success(`${hit.name} added to cart!`, {
                    theme: 'colored'
                });
            }

            setSearchTerm('');
            setSearchResults([]);

        } catch (error) {
            console.log(error);
            toast.error("Failed to update cart", { theme: 'colored' });
        }
    }

    async function handleTitleChange(e) {
        e.preventDefault();
        setEditTitle(false);
        let temp = e.target[0].value;
        try {
            
          await updateDoc(doc(db, "openOrders", viewingCartID), {
            orderBatchName: temp
          })
          
        } catch (error) {
          console.log(error);
        }
         
    }

    async function deleteCurrentOrder(e) {
        e.preventDefault();
        if (!cartId) {
            toast.error("Cannot delete: cart ID is missing", { theme: 'colored' });
            return;
        }
        try {
            await deleteDoc(doc(db, "openOrders", cartId))
            navigate(-1);
            toast.warn("Cart has been deleted!", {
                theme: 'colored'
              })
        } catch (error) {
          console.log(error);
          toast.error("Failed to delete cart", { theme: 'colored' });
        }

    }

    const handleDeleteClose = () => setDeleteCartState(false);

    useEffect(() => {
        if (loading) return;
        if (!debouncedSearchTerm) {
            setSearchResults([]);
            return;
        }
        searchProducts(debouncedSearchTerm);
    }, [debouncedSearchTerm, loading, fuzzyChoices, hideDiscontinued])
    
    const NoResult = () => {
        return <Alert variant='danger'>
            <p>This cart cannot be found or you do not have sufficient permissions to view it. </p>
            <Button onClick={() => {navigate('/batches')}}>Return to Batch Processing</Button>
        </Alert>
    }
    
    useEffect(() => {
        if (orderError) {
            console.error('Carts: order listener error', orderError);
            toast.error('Failed to load order data. Try refreshing.', { theme: 'colored', toastId: 'cart-order-error' });
        }
    }, [orderError]);

    useEffect(() => {
        if (authLoading) return;
        if (!user) return navigate('/');
        if (!cartId) return navigate(-1);
        setViewingCartID(cartId);

    }, [user, authLoading, cartId]);

    

    return (
        <>
            {
                !orderLoading ?
                    manualOrder ? 
                    <Container fluid>
                        <PageHeader
                            title="" titleTag="h4" className="mb-3">
                            <div className="position-relative" style={{height: '2.75rem'}}>
                                
                                <div className="position-absolute top-50 start-0">
                                        <h4>{manualOrder.orderBatchName}</h4>
                                        <p>Order Status: {manualOrder.status}</p>
                                </div>
                                <div className="position-absolute top-50 end-0">
                                        <FontAwesomeIcon className='ms-2' icon={pencil} onClick={() => setEditTitle(!editTitle)}/>
                                        {(manualOrder.uid === user?.uid || !manualOrder.uid) && (
                                            <FontAwesomeIcon className='ms-2' icon={trash} onClick={() => setDeleteCartState(!deleteCartState)}/>                    
                                        )}
                                </div>
                                
                            </div>
                            
                            {editTitle 
                                ?
                                <div className='mb-4 mt-4'>
                                <Form onSubmit={(e) => handleTitleChange(e)}>
                                    <InputGroup className="mb-3">
                                        <FormControl
                                            placeholder={manualOrder.orderBatchName}
                                            defaultValue={manualOrder.orderBatchName}
                                            aria-label="New Order/Batch Name"
                                            aria-describedby="basic-addon2"
                                        />
                                        <Button variant="outline-secondary" id="button-addon2" type="submit">
                                        Update
                                        </Button>
                                    </InputGroup>
                                </Form>
                            
                                </div>
                                :
                                null
                            }
                            
                        
                            <div className='mt-4'>
                                
                            </div>
                        </PageHeader>

                        <Modal show={deleteCartState} onHide={handleDeleteClose}>
                            <Modal.Header closeButton>
                                <Modal.Title>Delete Order?</Modal.Title>
                            </Modal.Header>
                                <Modal.Body>Please confirm you'd like to delete this order by pressing the delete button below.</Modal.Body>
                            <Modal.Footer>
                            <Button variant="secondary" onClick={handleDeleteClose}>
                                Close
                            </Button>
                            <Button 
                                variant="falcon-danger" 
                                onClick={(e) => deleteCurrentOrder(e)}>
                                Delete Order
                            </Button>
                            </Modal.Footer>
                        </Modal>

                        {!orderLoading && manualOrder.status !== "Submitted"
                            ?
                                <Card className='mb-3'>
                                <Card.Body>
                                
                                    <Form className='mb-1'>
                                        <Form.Control
                                            type="search"
                                            placeholder="Search for products..."
                                            aria-label="Search"
                                            className="rounded-pill search-input"
                                            value={searchTerm}
                                            onChange={handleSearchChange}

                                        />
                                    </Form>
                                    <div className='d-flex justify-content-between align-items-center mt-2 mb-2'>
                                        <Form.Check
                                            type="switch"
                                            id="hide-discontinued-switch"
                                            label="Hide discontinued items"
                                            checked={hideDiscontinued}
                                            onChange={(e) => setHideDiscontinued(e.target.checked)}
                                        />
                                        {searchResults.length > 0 && (
                                            <small className="text-muted">{searchResults.length} results</small>
                                        )}
                                    </div>
                                        {searchTerm.length > 0 ?
                                                <div className='mb-4'>
                                                    <ListGroup>
                                                        {searchResults.map((hit, index) => {
                                                            const product = hit[0];
                                                            const productImage = getProductImage(product) || MissingImage;
                                                            const isDiscontinued = product?.disc === true;
                                                            return (
                                                                <ListGroup.Item
                                                                    key={index}
                                                                    className={`d-flex justify-content-between align-items-center m-0${isDiscontinued ? ' bg-soft-danger' : ''}`}
                                                                    onClick={() => addToCurrentCart(product)}
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    <div className='d-flex align-items-center'>
                                                                        <Image
                                                                            src={productImage}
                                                                            alt={product?.name}
                                                                            height={50}
                                                                            />
                                                                        <div className='ms-2'>
                                                                            <p className='mb-0'>
                                                                                {isDiscontinued && <span className='me-1'>🛑</span>}
                                                                                {product?.name}
                                                                            </p>
                                                                            <p className='mb-0 fs--2 text-muted'>SKU: {product?.sku}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <p className='mb-0'>${hit.ibmi_cost}</p>
                                                                    </div>
                                                                </ListGroup.Item>
                                                            )
                                                        })}
                                                    </ListGroup>
                                                </div>
                                            :
                                                null
                                        }
                                    

                                
                                
                                </Card.Body>
                                </Card>
                            :
                                null
                        }

                        {manualOrder.status !== "Submitted"
                        ?
                                !orderLoading
                                    ? <ShoppingCart 
                                        items={manualOrder.items} 
                                        cartId={manualOrder.docId} 
                                        batchName={manualOrder.orderBatchName}
                                        org={orgData}                        
                                    /> 
                                    : <Spinner />
                            :
                                <Card className="p-3">
                                    <Alert variant="success">
                                        Thank you! Your order has been submitted.
                                    </Alert>
                                </Card>

                        }
                    </Container>
                    : <NoResult /> 
                :
                    <Spinner />
            }
        </>
    )

}



export default Carts;